import { create } from "zustand";
import { CollapsibleState } from "~/components/build/Show/components/Sidebar/TreeItem/CollapsibleState";
import { Step, isGroupStep, isWaitStep } from "~/lib/pipeline";
import { Build, Job } from "~/components/build/Show/lib/types";
import {
  getRelevantFilterForStep,
  sortSteps,
} from "~/components/build/Show/components/Sidebar/useFilterStore";

interface BuildStore {
  steps: Step[];
  stepsMap: Map<string, Step>;
  stepsGroupedByParent: Map<string, Step[]>;
  stepsGroupedByState: Map<string, Step[]>;
  setSteps: (steps: Step[], build: Build) => void;

  jobs: Map<string, Job>;
  stepJobs: Map<string, Job[]>;
  setJobs: (jobs: Job[]) => void;

  /**
   * Expanded sidebar step ids (ie. group/matrix/parallel)
   */
  expandedStepIds: Map<string, CollapsibleState>;
  showHiddenJobs: (id: string) => void;
  expandStep: (id: string) => void;
  toggleStep: (id: string) => void;
  setCollapsibleState: (id: string, state: CollapsibleState) => void;

  /**
   * Canvas groups
   */
  expandedGroupSteps: Set<string>;
  expandGroupSteps: (uuids: string[]) => void;
  collapseGroupSteps: (uuids: string[]) => void;
  isGroupStepExpanded: (uuid: string) => boolean;

  /**
   * Step search state
   */
  isSearchOpen: boolean;
  setSearchOpen: (open: boolean) => void;

  tableSearchValue: string;
  setTableSearchValue: (value: string) => void;

  /**
   * Active job tab (e.g. output, annotations, artifacts)
   */
  activeJobTab: string | null;
  setActiveJobTab: (tab: string | null) => void;
}

/**
 * Store for ephemeral build page-related state.
 */
export const useBuildStore = create<BuildStore>((set, get) => ({
  jobs: new Map<string, Job>(),
  stepJobs: new Map<string, Job[]>(),

  setJobs: (jobs: Job[]) => {
    const jobsMap = new Map([...get().jobs]);
    const stepJobsMap = new Map([...get().stepJobs]);

    for (const job of jobs) {
      const existingJob = jobsMap.get(job.id);

      if (existingJob) {
        // Update the existing job in both maps
        jobsMap.set(job.id, job);
        const stepJobs = stepJobsMap.get(job.stepUuid) || [];
        const jobIndex = stepJobs.findIndex((_job) => _job.id === job.id);
        if (jobIndex !== -1) {
          stepJobs[jobIndex] = job;
        }
      } else {
        // Add new job
        const stepJobs = stepJobsMap.get(job.stepUuid) || [];
        stepJobs.push(job);
        jobsMap.set(job.id, job);
        stepJobsMap.set(job.stepUuid, stepJobs);
      }
    }

    set({ jobs: jobsMap, stepJobs: stepJobsMap });
  },

  steps: [],
  stepsMap: new Map(),
  stepsGroupedByParent: new Map(),
  stepsGroupedByState: new Map(),

  /**
   * Add/replace steps in the store whilst also grouping them by state.
   *
   * `Build` is required for determining the "resolved state" of each step.
   *
   * todo: Handle replacing steps (via `--replace`)
   */
  setSteps: (input: Step[], build: Build) => {
    const stepsMap = get().stepsMap;
    const stepsGroupedByParent = new Map<string, Step[]>();
    const stepsGroupedByState = new Map<string, Step[]>();

    let hasAddedSteps = false;

    for (const step of input) {
      if (!stepsMap.has(step.uuid)) {
        hasAddedSteps = true;
      }
      stepsMap.set(step.uuid, step);
    }

    const steps = Array.from(stepsMap.values());
    const sortedStepsMap = new Map<string, Step>();

    // Since the store can have a subset of the steps, when new steps are added in (ie. switch display modes, expanding groups),
    // we need to re-sort to ensure they're in the correct order.
    if (hasAddedSteps) {
      sortSteps(steps);
    }

    /**
     * Iterate over all steps and group them.
     */
    steps.forEach((step) => {
      if (hasAddedSteps) {
        sortedStepsMap.set(step.uuid, step);
      }

      // Group steps by parent.
      if (step.groupUuid) {
        const children = stepsGroupedByParent.get(step.groupUuid) || [];
        children.push(step);
        stepsGroupedByParent.set(step.groupUuid, children);
      }

      // Group steps by state.
      if (!isGroupStep(step) && !isWaitStep(step)) {
        const state = getRelevantFilterForStep(step, build);
        if (state) {
          if (!stepsGroupedByState.has(state)) {
            stepsGroupedByState.set(state, []);
          }
          stepsGroupedByState.get(state)?.push(step);
        }
      }
    });

    set({
      steps,
      stepsMap: hasAddedSteps ? sortedStepsMap : stepsMap,
      stepsGroupedByParent,
      stepsGroupedByState,
    });
  },

  isSearchOpen: false,
  setSearchOpen: (open: boolean) => set(() => ({ isSearchOpen: open })),
  tableSearchValue: "",
  setTableSearchValue: (value: string) => set(() => ({ tableSearchValue: value })),

  setCollapsibleState: (id: string, collapsibleState: CollapsibleState) => {
    set((state) => {
      const expandedStepIds = new Map(state.expandedStepIds);
      expandedStepIds.set(id, collapsibleState);
      return { expandedStepIds };
    });
  },

  expandedStepIds: new Map<string, CollapsibleState>(),
  showHiddenJobs: (id: string) => {
    set((state) => {
      const expandedStepIds = new Map(state.expandedStepIds);
      expandedStepIds.set(id, CollapsibleState.Full);
      return { expandedStepIds };
    });
  },
  expandStep: (id: string) => {
    set((state) => {
      const expandedStepIds = new Map(state.expandedStepIds);
      if (expandedStepIds.get(id) !== CollapsibleState.Full) {
        expandedStepIds.set(id, CollapsibleState.Expanded);
      }
      return { expandedStepIds };
    });
  },
  toggleStep: (id: string) => {
    set((state) => {
      const expandedStepIds = new Map(state.expandedStepIds);
      expandedStepIds.set(
        id,
        expandedStepIds.get(id) === CollapsibleState.Expanded ||
          expandedStepIds.get(id) === CollapsibleState.Full
          ? CollapsibleState.Collapsed
          : CollapsibleState.Expanded,
      );
      return { expandedStepIds };
    });
  },

  expandedGroupSteps: new Set<string>(),

  expandGroupSteps: (uuids: string[]) => {
    set((state) => {
      const expandedGroupSteps = new Set(state.expandedGroupSteps);
      uuids.forEach((uuid) => expandedGroupSteps.add(uuid));
      return { expandedGroupSteps };
    });
  },

  collapseGroupSteps: (uuids: string[]) => {
    set((state) => {
      const expandedGroupSteps = new Set(state.expandedGroupSteps);
      uuids.forEach((uuid) => expandedGroupSteps.delete(uuid));
      return { expandedGroupSteps };
    });
  },

  isGroupStepExpanded: (uuid: string) => {
    return get().expandedGroupSteps.has(uuid);
  },

  activeJobTab: null,
  setActiveJobTab: (tab: string | null) => set(() => ({ activeJobTab: tab })),
}));
